import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Card,
  CardContent,
  Typography,
  Tooltip,
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import axios from "axios";
import Loader from "../../../components/loader/Loader";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExcelJS from "exceljs";

const generateYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear; y >= 2022; y--) years.push(y);
  return years;
};

function YearlyReport() {
  const [loader, setLoader] = useState(false);
  const [formData, setFormData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [allYearsData, setAllYearsData] = useState([]);
  const [yearObj, setYearObj] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL"); // ✅ NEW

  const loginType = sessionStorage.getItem("loginType") || "admin";
  const loggedInShopId = Number(sessionStorage.getItem("shop_id")) || 0;

  const [selectedYear, setSelectedYear] = useState("ALL");
  const [region, setRegion] = useState("");
  const [shop, setShop] = useState("");

  const regions = useSelector((state) => state.counter.allRegionList);
  const allShops = useSelector((state) => state.counter.shopList);
  const [filteredShops, setFilteredShops] = useState([]);
  const [filteredRegions, setFilteredRegions] = useState([]);

  const years = generateYears();

  // ── shop login init ──────────────────────────────────────────────────
  useEffect(() => {
    if (loginType === "shop" && loggedInShopId) {
      setShop(loggedInShopId);
      const sd = allShops.find((s) => s.shop_details_id === loggedInShopId);
      if (sd?.region_id) setRegion(sd.region_id);
    }
  }, [loginType, loggedInShopId, allShops]);

  // ── load on filter change ────────────────────────────────────────────
  useEffect(() => {
    loadReport();
  }, [selectedYear, region, shop]);

  // ── filter regions ───────────────────────────────────────────────────
  useEffect(() => {
    if (loginType === "shop" && loggedInShopId) {
      const sd = allShops.find((s) => s.shop_details_id === loggedInShopId);
      setFilteredRegions(
        sd?.region_id
          ? regions.filter((r) => r.region_id === sd.region_id)
          : [],
      );
    } else {
      setFilteredRegions(regions);
    }
  }, [loginType, loggedInShopId, regions, allShops]);

  // ── filter shops ─────────────────────────────────────────────────────
  useEffect(() => {
    if (loginType === "shop" && loggedInShopId) {
      const sd = allShops.find((s) => s.shop_details_id === loggedInShopId);
      setFilteredShops(sd ? [sd] : []);
    } else if (region) {
      setFilteredShops(
        allShops.filter((s) => s.region_id === parseInt(region)),
      );
    } else {
      setFilteredShops(allShops);
    }
  }, [loginType, loggedInShopId, region, allShops]);

  // ── API call ─────────────────────────────────────────────────────────
  const loadReport = () => {
    setLoader(true);
    const params = {};
    if (selectedYear !== "ALL") params.year = parseInt(selectedYear);
    if (region) params.region_id = parseInt(region);
    if (shop) params.shop_id = parseInt(shop);

    axios
      .get("/reports/salesTotal", { params })
      .then((res) => {
        const data = res?.data?.data || [];
        const arr = Array.isArray(data) ? data : [data];
        setAllYearsData(arr);
        buildTableData(arr, selectedYear);
      })
      .catch(() => {
        setFormData([]);
        setYearObj(null);
        setAllYearsData([]);
        setTotalCount(0);
      })
      .finally(() => setLoader(false));
  };

  // ── Build table rows ─────────────────────────────────────────────────
  const buildTableData = (arr, year) => {
    if (year === "ALL") {
      const rows = [];
      arr.forEach((y) => {
        (y.months || []).forEach((m) => {
          rows.push({
            period: `${m.month} ${y.year}`,
            total_orders: m.total_orders || 0,
            total_sales: m.total_sales || 0,
            delivered_orders: m.delivered_orders || 0,
            cancelled_orders: m.cancelled_orders || 0,
            failed_orders: m.failed_orders || 0,
            failed_details: m.failed_details || [],
          });
        });
      });

      const agg = {
        year_total_sales: rows.reduce(
          (s, r) => s + Number(r.total_sales || 0),
          0,
        ),
        year_total_orders: rows.reduce(
          (s, r) => s + Number(r.total_orders || 0),
          0,
        ),
        year_delivered_orders: rows.reduce(
          (s, r) => s + Number(r.delivered_orders || 0),
          0,
        ),
        year_cancelled_orders: rows.reduce(
          (s, r) => s + Number(r.cancelled_orders || 0),
          0,
        ),
      };

      setFormData(rows);
      setYearObj(agg);
      setTotalCount(rows.length);
    } else {
      const found = arr.find((d) => String(d.year) === year) || arr[0] || null;
      const months = (found?.months || []).map((m) => ({
        period: m.month,
        total_orders: m.total_orders || 0,
        total_sales: m.total_sales || 0,
        delivered_orders: m.delivered_orders || 0,
        cancelled_orders: m.cancelled_orders || 0,
        failed_orders: m.failed_orders || 0,
        failed_details: m.failed_details || [],
      }));

      setFormData(months);
      setYearObj(found);
      setTotalCount(months.length);
    }
  };

  // ✅ Apply status filter on top of formData
  const filteredFormData = formData.filter((row) => {
    if (statusFilter === "ALL") return true;
    if (statusFilter === "DELIVERED") return Number(row.delivered_orders) > 0;
    if (statusFilter === "CANCELLED") return Number(row.cancelled_orders) > 0;
    if (statusFilter === "FAILED") return Number(row.failed_orders) > 0;
    return true;
  });

  const formatIndianCurrency = (num) => {
    const n = parseFloat(num) || 0;
    return n.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getRegionName = (id) =>
    regions.find((r) => r.region_id === id)?.city?.name || "-";

  const totalFailed = formData.reduce(
    (sum, m) => sum + Number(m.failed_orders || 0),
    0,
  );

  // ── Excel export ─────────────────────────────────────────────────────
  const handleCustomDownload = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const sheetName =
        selectedYear === "ALL"
          ? "All Months Report"
          : `Yearly Report ${selectedYear}`;
      const worksheet = workbook.addWorksheet(sheetName);

      // ✅ Helper
      const toNumber = (val) => {
        const n = parseFloat(val);
        return isNaN(n) ? 0 : n;
      };

      worksheet.columns = [
        { header: "Month", key: "period", width: 15 },
        { header: "Total Orders", key: "total_orders", width: 15 },
        { header: "Total Sales (₹)", key: "total_sales", width: 20 },
        { header: "Delivered Orders", key: "delivered_orders", width: 18 },
        { header: "Cancelled Orders", key: "cancelled_orders", width: 18 },
        { header: "Failed Orders", key: "failed_orders", width: 15 },
      ];

      // ✅ Numbers as numbers, text as text
      filteredFormData.forEach((m) => {
        worksheet.addRow({
          period: m.period || "_", // text
          total_orders: toNumber(m.total_orders), // number
          total_sales: toNumber(m.total_sales), // number
          delivered_orders: toNumber(m.delivered_orders), // number
          cancelled_orders: toNumber(m.cancelled_orders), // number
          failed_orders: toNumber(m.failed_orders), // number
        });
      });

      // ✅ Summary row
      worksheet.addRow({});
      const summaryRow = worksheet.addRow({
        period: "TOTAL",
        total_orders: toNumber(yearObj?.year_total_orders),
        total_sales: toNumber(yearObj?.year_total_sales),
        delivered_orders: toNumber(yearObj?.year_delivered_orders),
        cancelled_orders: toNumber(yearObj?.year_cancelled_orders),
        failed_orders: toNumber(totalFailed),
      });
      summaryRow.font = { bold: true };

      // ✅ Header styling
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.alignment = { horizontal: "center", vertical: "middle" };
      headerRow.height = 20;

      // ✅ Center align all data cells
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          row.eachCell((cell) => {
            cell.alignment = { horizontal: "center", vertical: "middle" };
          });
        }
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download =
        selectedYear === "ALL"
          ? "All_Months_Report.xlsx"
          : `Yearly_Report_${selectedYear}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Excel error:", err);
      alert("Failed to generate Excel file.");
    }
  };

  // ── MUIDataTable columns ─────────────────────────────────────────────
  const columns = [
    {
      name: "id",
      label: "S. No",
      options: {
        customBodyRenderLite: (dataIndex) => <p>{dataIndex + 1}</p>,
      },
    },
    {
      name: "period",
      label: "Month",
      options: {
        customBodyRenderLite: (i) => (
          <Typography variant="body2">
            {filteredFormData[i]?.period || "-"}
          </Typography>
        ),
      },
    },
    {
      name: "total_orders",
      label: "Total Orders",
      options: {
        customBodyRenderLite: (i) => (
          <Chip
            label={filteredFormData[i]?.total_orders || 0}
            size="small"
            sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 600 }}
          />
        ),
      },
    },
    {
      name: "total_sales",
      label: "Total Sales",
      options: {
        customBodyRenderLite: (i) => {
          const amt = filteredFormData[i]?.total_sales;
          return (
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {amt ? `₹${formatIndianCurrency(amt)}` : "-"}
            </Typography>
          );
        },
      },
    },
    {
      name: "delivered_orders",
      label: "Delivered",
      options: {
        setCellHeaderProps: () => ({
          style: { textAlign: "center" },
        }),
        setCellProps: () => ({
          style: { textAlign: "center" },
        }),
        customBodyRenderLite: (i) => (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "25px",
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#2e7d32" }} />
            <Typography
              variant="body2"
              sx={{
                color: "#2e7d32",
                fontWeight: 600,
                lineHeight: 1, // 👈 remove extra vertical space
              }}
            >
              {filteredFormData[i]?.delivered_orders || 0}
            </Typography>
          </Box>
        ),
      },
    },
    {
      name: "cancelled_orders",
      label: "Cancelled",
      options: {
        setCellHeaderProps: () => ({
          align: "center",
        }),
        setCellProps: () => ({
          align: "center",
        }),
        customBodyRenderLite: (i) => (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "25px",
            }}
          >
            <CancelOutlinedIcon sx={{ fontSize: 16, color: "#c62828" }} />
            <Typography
              variant="body2"
              sx={{
                color: "#c62828",
                fontWeight: 600,
                lineHeight: 1,
              }}
            >
              {filteredFormData[i]?.cancelled_orders || 0}
            </Typography>
          </Box>
        ),
      },
    },
    {
      name: "failed_orders",
      label: "Failed",
      options: {
        filter: false,
        download: false,
        customBodyRenderLite: (i) => {
          const row = filteredFormData[i];
          const failedDetails = row?.failed_details || [];

          const tooltipContent =
            failedDetails.length > 0 ? (
              <div style={{ padding: "4px", minWidth: 220 }}>
                <div style={{ marginBottom: "6px", fontWeight: 700 }}>
                  Failed Details — {row?.period}
                </div>
                {failedDetails.map((fd, idx) => (
                  <div
                    key={idx}
                    style={{
                      marginBottom: "4px",
                      borderBottom: "1px solid rgba(255,255,255,0.2)",
                      paddingBottom: "4px",
                    }}
                  >
                    <div>
                      <strong>Status:</strong> {fd.checkout_status}
                    </div>
                    <div>
                      <strong>Message:</strong> {fd.message}
                    </div>
                    <div>
                      <strong>Count:</strong> {fd.count}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              "No failed details"
            );

          return (
            <Tooltip
              title={tooltipContent}
              arrow
              placement="bottom"
              PopperProps={{
                modifiers: [
                  {
                    name: "flip",
                    enabled: true,
                    options: { fallbackPlacements: ["top", "right", "left"] },
                  },
                  {
                    name: "preventOverflow",
                    enabled: true,
                    options: { boundary: "window" },
                  },
                ],
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  justifyContent: "center",
                  cursor: failedDetails.length > 0 ? "pointer" : "default",
                }}
              >
                <ErrorOutlineIcon sx={{ fontSize: 16, color: "#ed6c02" }} />
                <Typography
                  variant="body2"
                  sx={{ color: "#ed6c02", fontWeight: 600 }}
                >
                  {row?.failed_orders || 0}
                </Typography>
              </Box>
            </Tooltip>
          );
        },
      },
    },
  ];

  const options = {
    filter: false,
    search: true,
    selectableRows: "none",
    rowsPerPage: 12,
    count: filteredFormData.length,
    serverSide: false,
    print: false,
    viewColumns: false,
    download: true,
    onDownload: () => {
      handleCustomDownload();
      return false;
    },
    textLabels: {
      body: {
        noMatch: loader ? "Loading..." : "No records found",
      },
    },
  };

  // ✅ Status chip config
  const statusOptions = [
    { value: "ALL", label: "All", color: "#1565c0", bg: "#e3f2fd" },
    { value: "DELIVERED", label: "Delivered", color: "#2e7d32", bg: "#e8f5e9" },
    { value: "CANCELLED", label: "Cancelled", color: "#c62828", bg: "#ffebee" },
    { value: "FAILED", label: "Failed", color: "#ed6c02", bg: "#fff3e0" },
  ];

  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          {/* ── Header ── */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 2,
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* <CalendarMonthIcon color="primary" /> */}
              <h4 className="text-dark">Yearly Report</h4>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Chip
                label={
                  selectedYear === "ALL" ? "All Years" : `Year: ${selectedYear}`
                }
                color="primary"
                size="small"
              />
              {filteredFormData.length > 0 && (
                <Chip
                  label={`${filteredFormData.length} months`}
                  color="success"
                  size="small"
                />
              )}
            </Box>
          </Box>

          {/* ── Filters ── */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Year */}
            <Grid item xs={12} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Year</InputLabel>
                <Select
                  value={selectedYear}
                  label="Year"
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  <MenuItem value="ALL">
                    <em>All Years</em>
                  </MenuItem>
                  {years.map((y) => (
                    <MenuItem key={y} value={String(y)}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Region */}
            <Grid item xs={12} md={2.4}>
              <FormControl
                fullWidth
                size="small"
                disabled={loginType === "shop"}
              >
                <InputLabel>ALL Region</InputLabel>
                <Select
                  value={region}
                  label="Region"
                  onChange={(e) => {
                    setRegion(e.target.value);
                    if (loginType !== "shop") setShop("");
                  }}
                >
                  <MenuItem value="">All Regions</MenuItem>
                  {filteredRegions?.map((r) => (
                    <MenuItem key={r.region_id} value={r.region_id}>
                      {r.city?.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Shop */}
            {/* <Grid item xs={12} md={2.4}>
              <FormControl fullWidth size="small" disabled={loginType === "shop"}>
                <InputLabel>ALL Shop</InputLabel>
                <Select
                  value={shop}
                  label="Shop"
                  onChange={(e) => setShop(e.target.value)}
                >
                  <MenuItem value="">All Shops</MenuItem>
                  {filteredShops.map((s) => (
                    <MenuItem key={s.shop_details_id} value={s.shop_details_id}>
                      {s.shop_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid> */}

            {/* ✅ Status Filter Chips */}
            <Grid item xs={12} md={4.8}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  height: "100%",
                  flexWrap: "wrap",
                }}
              >
                {statusOptions.map((opt) => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    onClick={() => setStatusFilter(opt.value)}
                    sx={{
                      fontWeight: 600,
                      cursor: "pointer",
                      bgcolor: statusFilter === opt.value ? opt.color : opt.bg,
                      color: statusFilter === opt.value ? "#fff" : opt.color,
                      border: `1.5px solid ${opt.color}`,
                      "&:hover": { bgcolor: opt.color, color: "#fff" },
                      transition: "all 0.2s",
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>

          {/* ── Main Content ── */}
          <Grid container spacing={2}>
            {/* MUIDataTable */}
            <Grid item xs={12} md={8}>
              <MUIDataTable
                data={filteredFormData}
                columns={columns}
                options={options}
              />
            </Grid>

            {/* Side Cards */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Total Sales blue card */}
                {loginType !== "shop" && (
                  <Card sx={{ bgcolor: "#1976d2", color: "white" }}>
                    <CardContent>
                      <Typography
                        variant="body2"
                        sx={{ opacity: 0.9, mb: 0.5 }}
                      >
                        Total Sales —{" "}
                        {selectedYear === "ALL" ? "All Years" : selectedYear}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 700, mb: 0.5 }}
                      >
                        ₹{formatIndianCurrency(yearObj?.year_total_sales || 0)}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          {yearObj?.year_delivered_orders || 0} delivered
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ opacity: 0.8, color: "#ffcdd2" }}
                        >
                          {yearObj?.year_cancelled_orders || 0} cancelled
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Order Summary card */}
                {loginType !== "shop" && (
                  <Card>
                    <CardContent>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, mb: 2, color: "#666" }}
                      >
                        Order Summary{" "}
                        {region && `- ${getRegionName(parseInt(region))}`}
                      </Typography>

                      <Box
                        sx={{
                          p: 1.5,
                          mb: 1.5,
                          bgcolor: "#e8f5e9",
                          borderRadius: 1,
                          borderLeft: "4px solid #2e7d32",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Delivered Orders
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, color: "#2e7d32" }}
                        >
                          {yearObj?.year_delivered_orders || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          out of {yearObj?.year_total_orders || 0} total
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          p: 1.5,
                          mb: 1.5,
                          bgcolor: "#ffebee",
                          borderRadius: 1,
                          borderLeft: "4px solid #c62828",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Cancelled Orders
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, color: "#c62828" }}
                        >
                          {yearObj?.year_cancelled_orders || 0}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: "#fff3e0",
                          borderRadius: 1,
                          borderLeft: "4px solid #ed6c02",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Failed / Incomplete Payments
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, color: "#ed6c02" }}
                        >
                          {totalFailed}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Hover on Failed column for details
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Sales bar chart — unchanged */}
                <Card>
                  <CardContent>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1.5, color: "#666" }}
                    >
                      {selectedYear === "ALL"
                        ? "All Months Sales"
                        : "Month-wise Sales"}
                    </Typography>
                    <Box
                      sx={{
                        maxHeight: loginType === "shop" ? 450 : 350,
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      {filteredFormData.length === 0 ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textAlign: "center", py: 2 }}
                        >
                          No data available
                        </Typography>
                      ) : (
                        (() => {
                          const maxSales = Math.max(
                            ...filteredFormData.map(
                              (m) => parseFloat(m.total_sales) || 0,
                            ),
                          );
                          return filteredFormData.map((m, idx) => {
                            const pct =
                              maxSales > 0
                                ? ((parseFloat(m.total_sales) || 0) /
                                    maxSales) *
                                  100
                                : 0;
                            return (
                              <Box key={idx}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    mb: 0.3,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {m.period}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "#1565c0", fontWeight: 600 }}
                                  >
                                    ₹{formatIndianCurrency(m.total_sales)}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    height: 6,
                                    bgcolor: "#e3f2fd",
                                    borderRadius: 3,
                                    overflow: "hidden",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      height: "100%",
                                      width: `${pct}%`,
                                      bgcolor: "#1976d2",
                                      borderRadius: 3,
                                      transition: "width 0.5s ease",
                                    }}
                                  />
                                </Box>
                              </Box>
                            );
                          });
                        })()
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
}

export default YearlyReport;
